import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const CONSENT_KEY = "cookie_consent";
const CONSENT_EXPIRY_DAYS = 180;

function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() > data.expires) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setConsent(accepted) {
  const data = {
    accepted,
    analytics: false,
    date: new Date().toISOString(),
    expires: Date.now() + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) setVisible(true);
  }, []);

  const handleAccept = () => {
    setConsent(true);
    setVisible(false);
  };

  const handleRefuse = () => {
    setConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
      data-testid="cookie-banner"
    >
      <div className="max-w-2xl mx-auto bg-white/95 backdrop-blur-xl border border-[#E5E1D8] rounded-2xl shadow-lg p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center mt-0.5">
            <Cookie size={20} className="text-terracotta" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-serif-display text-lg text-[#1F1C18]">
                Ce site utilise des cookies
              </h3>
              <button
                onClick={handleRefuse}
                className="shrink-0 text-[#6B655C] hover:text-[#1F1C18] transition"
                aria-label="Fermer"
                data-testid="cookie-banner-close"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-sm text-[#6B655C] leading-relaxed mt-1.5">
              Nous utilisons uniquement des cookies essentiels pour le bon fonctionnement du site
              (session, panier, paiement). Aucun cookie publicitaire n'est utilisé.{" "}
              <Link
                to="/cookies"
                className="text-terracotta underline hover:text-terracotta/80"
                data-testid="cookie-banner-policy-link"
              >
                En savoir plus
              </Link>
            </p>

            <div className="flex flex-wrap gap-3 mt-4">
              <button
                onClick={handleAccept}
                className="px-5 py-2 rounded-full text-sm font-medium bg-terracotta text-white hover:bg-terracotta/90 transition"
                data-testid="cookie-accept-btn"
              >
                Accepter
              </button>
              <button
                onClick={handleRefuse}
                className="px-5 py-2 rounded-full text-sm font-medium border border-[#E5E1D8] text-[#6B655C] hover:border-terracotta hover:text-terracotta transition"
                data-testid="cookie-refuse-btn"
              >
                Refuser les optionnels
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
